import { useState } from 'react';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { Activity, ShieldAlert, ShieldCheck } from 'lucide-react';
import { Breadcrumbs, BreadcrumbItem, Container, PageHeader, SplitView } from '@vnetwork/ui/layout';
import {
  Button,
  Stepper,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  Input,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useCreateWatchpoint,
  useAlarmFieldCatalogByType,
  useCreateAlarm,
  PROTOCOL_TYPES,
  type WatchpointType,
  type AlarmSeverity,
} from '@/api/healthcheck';
import { WATCHPOINT_TYPES, typeMeta, targetSummary, fmtSeconds } from '../shared';
import { WatchpointConfigForm, isConfigValid, applyConfigDefaults, type ConfigState } from './WatchpointConfigForms';
import type { WatchpointPrefillState } from '../onboarding/sample-monitor';
import { MONITOR_TEMPLATES, TEMPLATE_CATEGORIES, type MonitorTemplate } from './monitor-templates';
import { WatchpointAlarmStep } from './WatchpointAlarmStep';
import { serializeConditions, validateRows, type ConditionRow } from '../alarms/condition-serializer';
import { brand } from '@/config/brand';

const STEPS = ['Type', 'Configure', 'Alarm', 'Review'];

export function WatchpointCreate() {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const base = `/services/healthcheck/${projectId}`;
  const { show } = useToast();

  // Optional prefill (e.g. the onboarding "Sample monitor") passed via route-state.
  // Seeds the wizard's initial values so the same form is reused, pre-filled.
  const { state } = useLocation();
  const prefill = (state ?? null) as WatchpointPrefillState | null;

  const initialStep: number = prefill?.startStep ?? 0;
  const [step, setStep] = useState(initialStep);
  const [reached, setReached] = useState(initialStep);
  const [type, setType] = useState<WatchpointType | null>(prefill?.type ?? null);
  // biSource=true when the user picks the merged "WAAP / BI" card. The actual
  // submit type is derived from whichever metrics the user selects (waap|delivery).
  // Non-BI selections always set biSource=false.
  const [biSource, setBiSource] = useState<boolean>(
    prefill?.type === 'waap' || prefill?.type === 'delivery',
  );
  const [name, setName] = useState(prefill?.name ?? '');
  const [config, setConfig] = useState<ConfigState>(prefill?.config ?? {});
  const [interval, setIntervalSec] = useState(prefill?.check_interval ?? 60);
  const [timeout, setTimeoutSec] = useState(prefill?.timeout_seconds ?? 30);
  const [showErrors, setShowErrors] = useState(false);

  // Optional starter alarm created together with the monitor (seeded by templates).
  const [alarmEnabled, setAlarmEnabled] = useState(false);
  const [alarmName, setAlarmName] = useState('');
  const [alarmSeverity, setAlarmSeverity] = useState<AlarmSeverity>('warning');
  const [alarmRows, setAlarmRows] = useState<ConditionRow[]>([]);
  const [alarmMode, setAlarmMode] = useState<'and' | 'or'>('and');
  const [alarmChannelIds, setAlarmChannelIds] = useState<string[]>([]);

  // Alarm field catalog for the chosen type — drives the condition builder and the
  // eval-level used to serialise conditions at submit time.
  const { data: alarmCatalog, isLoading: alarmCatalogLoading } = useAlarmFieldCatalogByType(type ?? undefined);

  const createAlarm = useCreateAlarm({
    onSuccess: (alarm) => {
      show({ tone: 'success', title: `Alarm "${alarm.name}" created` });
      navigate(`${base}/monitors/${alarm.watchpoint_id}`);
    },
    onError: (e) => {
      // Monitor already created — surface the alarm failure but still land on it.
      show({ tone: 'danger', title: (e as Error)?.message || 'Alarm create failed' });
    },
  });

  const create = useCreateWatchpoint(projectId ?? '', {
    onSuccess: (wp) => {
      show({ tone: 'success', title: `Monitor "${wp.name}" created` });
      // Chain the starter alarm when enabled + valid; otherwise land on the monitor.
      if (alarmEnabled && wp?.id && alarmCatalog && alarmName.trim() && validateRows(alarmRows).length === 0) {
        createAlarm.mutate({
          watchpoint_id: wp.id,
          name: alarmName.trim(),
          severity: alarmSeverity,
          conditions: serializeConditions(alarmRows, alarmCatalog.eval_level, alarmMode),
          consecutive_failures: 3,
          cooldown_minutes: 5,
          channel_ids: alarmChannelIds,
        });
        return;
      }
      navigate(wp?.id ? `${base}/monitors/${wp.id}` : `${base}/monitors`);
    },
    onError: (e) => show({ tone: 'danger', title: (e as Error)?.message || 'Create failed' }),
  });

  const goto = (i: number) => {
    setStep(i);
    setReached((r) => Math.max(r, i));
  };

  // Apply a ready-made template: seed the form from its monitor prefill and jump
  // straight to Configure so the user just tweaks placeholders (target, etc.).
  const applyTemplate = (tpl: MonitorTemplate) => {
    const m = tpl.monitor;
    if (m.type) {
      setType(m.type);
      setBiSource(m.type === 'waap' || m.type === 'delivery');
    }
    setName(m.name ?? '');
    setConfig(m.config ?? {});
    setIntervalSec(m.check_interval ?? 60);
    setTimeoutSec(m.timeout_seconds ?? 30);
    // Seed the starter alarm from the template (or clear it for a bare type).
    if (tpl.alarm) {
      setAlarmEnabled(true);
      setAlarmName(tpl.alarm.name);
      setAlarmSeverity(tpl.alarm.severity);
      setAlarmRows(tpl.alarm.rows);
      setAlarmMode('and');
    } else {
      setAlarmEnabled(false);
      setAlarmRows([]);
    }
    setShowErrors(false);
    goto(1);
  };

  const nameOk = name.trim().length >= 3;
  const configOk = !!type && isConfigValid(type, config, alarmCatalog?.breakdown_metrics ?? []);
  const alarmOk = !alarmEnabled || (alarmName.trim().length > 0 && validateRows(alarmRows).length === 0);

  const next = () => {
    if (step === 0 && !type) return;
    if (step === 1 && (!nameOk || !configOk)) {
      setShowErrors(true);
      return;
    }
    if (step === 2 && !alarmOk) {
      setShowErrors(true);
      return;
    }
    setShowErrors(false);
    goto(step + 1);
  };

  const submit = () => {
    if (!type || !nameOk || !configOk) {
      setShowErrors(true);
      setStep(1);
      return;
    }
    if (!alarmOk) {
      setShowErrors(true);
      setStep(2);
      return;
    }
    create.mutate({
      project_id: projectId ?? '',
      name: name.trim(),
      type,
      config: applyConfigDefaults(type, config),
      check_interval: interval,
      timeout_seconds: timeout,
    });
  };

  const summary = (
    <Card>
      <CardHeader>
        <CardTitle>Summary</CardTitle>
        <CardDescription>Review before creating.</CardDescription>
      </CardHeader>
      <CardBody>
        <dl style={{ display: 'grid', gap: 'var(--vn-space-2)', margin: 0 }}>
          <Row label="Type" value={type ? typeMeta(type).label : '—'} />
          <Row label="Name" value={name || '—'} />
          <Row label="Target" value={type ? targetSummary(type, config) : '—'} />
          <Row label="Interval" value={fmtSeconds(interval)} />
          <Row label="Timeout" value={fmtSeconds(timeout)} />
        </dl>
      </CardBody>
    </Card>
  );

  return (
    <Container size="xl">
      <PageHeader
        icon={<Activity size={20} />}
        breadcrumbs={
          <Breadcrumbs>
            <BreadcrumbItem asChild><Link to={`${base}/monitors`}>Monitors</Link></BreadcrumbItem>
            <BreadcrumbItem current>New monitor</BreadcrumbItem>
          </Breadcrumbs>
        }
        title="New monitor"
        description="Create a watchpoint to track an endpoint, port, certificate or data signal."
      />

      <div style={{ marginBottom: 'var(--vn-space-5)' }}>
        <Stepper steps={STEPS} current={step} reached={reached} onStepClick={goto} />
      </div>

      <SplitView aside={summary} asideWidth="320px">
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)', maxWidth: 640 }}>
          {step === 0 && (
            <>
              <TemplateGallery onPick={applyTemplate} />
              <div style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)', textAlign: 'center' }}>
                — or start from a blank monitor —
              </div>
              <TypeGroup heading="Protocol monitors" types={PROTOCOL_TYPES} selected={type} onSelect={(t) => { setType(t); setBiSource(false); }} />
              {/* Web security: WAF (Elastic log analytics) + WAAP (BI metrics) as linked VNETWORK services. */}
              <SecurityServicesGroup
                type={type}
                biSource={biSource}
                onSelectWaf={() => { setType('elastic'); setBiSource(false); }}
                onSelectWaap={() => { setType('waap'); setBiSource(true); }}
              />
              <ComingSoonCard
                heading="Custom"
                label="External / Kuma monitors"
                blurb="Bring your own probe or import from Uptime Kuma. Coming soon."
              />
            </>
          )}

          {step === 1 && type && (
            <>
              <Card flat>
                <CardHeader>
                  <CardTitle>Monitor details</CardTitle>
                  <CardDescription>Give it a recognizable name.</CardDescription>
                </CardHeader>
                <CardBody>
                  <Field label="Name" required error={showErrors && !nameOk ? 'At least 3 characters.' : undefined}>
                    {(p) => <Input {...p} value={name} onChange={(e) => setName(e.target.value)} placeholder="API health" style={{ maxWidth: 420 }} invalid={showErrors && !nameOk} />}
                  </Field>
                </CardBody>
              </Card>
              <Card flat>
                <CardHeader>
                  <CardTitle>{biSource ? 'WAAP' : typeMeta(type).label} configuration</CardTitle>
                  <CardDescription>{biSource ? 'Web App & API Protection — security, bot & delivery metrics.' : typeMeta(type).blurb}</CardDescription>
                </CardHeader>
                <CardBody>
                  <WatchpointConfigForm
                    type={type}
                    config={config}
                    onChange={(patch) => setConfig((c) => ({ ...c, ...patch }))}
                    showErrors={showErrors}
                    biSource={biSource}
                    onDerivedType={(derived) => setType(derived)}
                  />
                </CardBody>
              </Card>
              <Card flat>
                <CardHeader>
                  <CardTitle>Schedule</CardTitle>
                  <CardDescription>How often to run the check.</CardDescription>
                </CardHeader>
                <CardBody>
                  <div style={{ display: 'grid', gap: 'var(--vn-space-4)', gridTemplateColumns: 'repeat(auto-fit, minmax(min(160px, 100%), 1fr))', maxWidth: 420 }}>
                    <Field label="Interval (seconds)">
                      {(p) => <Input {...p} type="number" value={String(interval)} onChange={(e) => setIntervalSec(Number(e.target.value) || 60)} />}
                    </Field>
                    <Field label="Timeout (seconds)">
                      {(p) => <Input {...p} type="number" value={String(timeout)} onChange={(e) => setTimeoutSec(Number(e.target.value) || 30)} />}
                    </Field>
                  </div>
                </CardBody>
              </Card>
            </>
          )}

          {step === 2 && type && (
            <WatchpointAlarmStep
              catalog={alarmCatalog}
              catalogLoading={alarmCatalogLoading}
              enabled={alarmEnabled}
              onEnabledChange={setAlarmEnabled}
              name={alarmName}
              onNameChange={setAlarmName}
              severity={alarmSeverity}
              onSeverityChange={setAlarmSeverity}
              rows={alarmRows}
              onRowsChange={setAlarmRows}
              mode={alarmMode}
              onModeChange={setAlarmMode}
              channelIds={alarmChannelIds}
              onChannelIdsChange={setAlarmChannelIds}
              showErrors={showErrors}
            />
          )}

          {step === 3 && (
            <Card flat>
              <CardHeader>
                <CardTitle>Ready to create</CardTitle>
                <CardDescription>Confirm the summary on the right, then create the monitor.</CardDescription>
              </CardHeader>
              <CardBody>
                <p style={{ margin: 0, color: 'var(--vn-fg-muted)' }}>
                  The monitor starts running on its schedule immediately.
                  {alarmEnabled ? ' Its alarm is created alongside it.' : ' You can pause it anytime.'}
                </p>
              </CardBody>
            </Card>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--vn-space-2)' }}>
            <Button variant="ghost" onClick={() => (step === 0 ? navigate(`${base}/monitors`) : goto(step - 1))}>
              {step === 0 ? 'Cancel' : 'Back'}
            </Button>
            {step < 3 ? (
              <Button onClick={next} disabled={step === 0 && !type}>
                Continue
              </Button>
            ) : (
              <Button onClick={submit} disabled={create.isPending || createAlarm.isPending}>
                {alarmEnabled ? 'Create monitor + alarm' : 'Create monitor'}
              </Button>
            )}
          </div>
        </div>
      </SplitView>
    </Container>
  );
}

function TemplateGallery({ onPick }: { onPick: (t: MonitorTemplate) => void }) {
  return (
    <Card flat>
      <CardHeader>
        <CardTitle>Start from a template</CardTitle>
        <CardDescription>Ready-made monitors for common goals — pick one, then tweak the target.</CardDescription>
      </CardHeader>
      <CardBody>
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          {TEMPLATE_CATEGORIES.map((cat) => {
            const items = MONITOR_TEMPLATES.filter((t) => t.category === cat);
            if (items.length === 0) return null;
            return (
              <div key={cat}>
                <div style={{ fontSize: 'var(--vn-font-size-sm)', fontWeight: 600, color: 'var(--vn-fg-muted)', marginBottom: 'var(--vn-space-2)' }}>{cat}</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 'var(--vn-space-3)' }}>
                  {items.map((tpl) => {
                    const Icon = tpl.monitor.type ? WATCHPOINT_TYPES[tpl.monitor.type].icon : null;
                    return (
                      <button
                        key={tpl.id}
                        type="button"
                        onClick={() => onPick(tpl)}
                        style={{
                          textAlign: 'left',
                          cursor: 'pointer',
                          font: 'inherit',
                          color: 'var(--vn-fg-default)',
                          border: '1px solid var(--vn-border-default)',
                          borderRadius: 'var(--vn-radius-lg)',
                          background: 'var(--vn-bg-surface)',
                          padding: 'var(--vn-space-3)',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', marginBottom: 4 }}>
                          {Icon && <Icon size={16} />}
                          <span style={{ fontWeight: 600 }}>{tpl.label}</span>
                        </div>
                        <div style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>{tpl.description}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </CardBody>
    </Card>
  );
}

/** Web security services group: the two VNETWORK products presented as linked
 *  services — Web Application Firewall (WAF, Elastic log analytics) and Web App &
 *  API Protection (WAAP, BI metrics). WAF sets type='elastic'; WAAP sets a
 *  provisional type='waap' + biSource (real type derived from the picked metric). */
function SecurityServicesGroup({
  type,
  biSource,
  onSelectWaf,
  onSelectWaap,
}: {
  type: WatchpointType | null;
  biSource: boolean;
  onSelectWaf: () => void;
  onSelectWaap: () => void;
}) {
  const cardStyle = (active: boolean): React.CSSProperties => ({
    textAlign: 'left',
    cursor: 'pointer',
    font: 'inherit',
    color: 'var(--vn-fg-default)',
    border: `1px solid ${active ? 'var(--vn-primary-border)' : 'var(--vn-border-default)'}`,
    outline: active ? '1px solid var(--vn-primary-solid)' : 'none',
    borderRadius: 'var(--vn-radius-lg)',
    background: active ? 'var(--vn-primary-subtle)' : 'var(--vn-bg-surface)',
    padding: 'var(--vn-space-3)',
  });
  return (
    <Card flat>
      <CardHeader>
        <CardTitle>Web security</CardTitle>
        <CardDescription>Monitor your {brand.shortName} protection services.</CardDescription>
      </CardHeader>
      <CardBody>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 'var(--vn-space-3)' }}>
          <button type="button" onClick={onSelectWaf} style={cardStyle(type === 'elastic' && !biSource)}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', marginBottom: 4 }}>
              <ShieldAlert size={17} />
              <span style={{ fontWeight: 600 }}>Web Application Firewall (WAF)</span>
            </div>
            <div style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
              WAF log analytics — 5xx surges, DDoS, scans, rule blocks.
            </div>
          </button>
          <button type="button" onClick={onSelectWaap} style={cardStyle(biSource)}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', marginBottom: 4 }}>
              <ShieldCheck size={17} />
              <span style={{ fontWeight: 600 }}>Web App &amp; API Protection (WAAP)</span>
            </div>
            <div style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
              Security, Bot, WAF &amp; CDN metrics (BI).
            </div>
          </button>
        </div>
      </CardBody>
    </Card>
  );
}

/** A disabled placeholder source card for a not-yet-available integration. */
function ComingSoonCard({ heading, label, blurb }: { heading: string; label: string; blurb: string }) {
  return (
    <Card flat>
      <CardHeader>
        <CardTitle>{heading}</CardTitle>
      </CardHeader>
      <CardBody>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 'var(--vn-space-3)' }}>
          <div
            aria-disabled
            style={{
              textAlign: 'left',
              cursor: 'not-allowed',
              opacity: 0.55,
              border: '1px dashed var(--vn-border-default)',
              borderRadius: 'var(--vn-radius-lg)',
              background: 'var(--vn-bg-surface)',
              padding: 'var(--vn-space-3)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', marginBottom: 4 }}>
              <span style={{ fontWeight: 600 }}>{label}</span>
              <span style={{
                fontSize: 'var(--vn-font-size-xs)', lineHeight: 1, padding: '1px 6px',
                borderRadius: 'var(--vn-radius-full)', background: 'var(--vn-neutral-subtle)',
                color: 'var(--vn-fg-muted)', fontWeight: 600,
              }}>
                Coming soon
              </span>
            </div>
            <div style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>{blurb}</div>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

function TypeGroup({
  heading,
  types,
  selected,
  onSelect,
}: {
  heading: string;
  types: WatchpointType[];
  selected: WatchpointType | null;
  onSelect: (t: WatchpointType) => void;
}) {
  return (
    <Card flat>
      <CardHeader>
        <CardTitle>{heading}</CardTitle>
      </CardHeader>
      <CardBody>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 'var(--vn-space-3)' }}>
          {types.map((t) => {
            const meta = WATCHPOINT_TYPES[t];
            const Icon = meta.icon;
            const active = selected === t;
            return (
              <button
                key={t}
                type="button"
                onClick={() => onSelect(t)}
                style={{
                  textAlign: 'left',
                  cursor: 'pointer',
                  font: 'inherit',
                  color: 'var(--vn-fg-default)',
                  border: `1px solid ${active ? 'var(--vn-primary-border)' : 'var(--vn-border-default)'}`,
                  outline: active ? '1px solid var(--vn-primary-solid)' : 'none',
                  borderRadius: 'var(--vn-radius-lg)',
                  background: active ? 'var(--vn-primary-subtle)' : 'var(--vn-bg-surface)',
                  padding: 'var(--vn-space-3)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', marginBottom: 4 }}>
                  <Icon size={17} />
                  <span style={{ fontWeight: 600 }}>{meta.label}</span>
                </div>
                <div style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>{meta.blurb}</div>
              </button>
            );
          })}
        </div>
      </CardBody>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--vn-space-3)', fontSize: 'var(--vn-font-size-sm)' }}>
      <dt style={{ color: 'var(--vn-fg-muted)' }}>{label}</dt>
      <dd style={{ margin: 0, textAlign: 'right', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</dd>
    </div>
  );
}

